import { ExecutionResult } from 'graphql';
import { extractDataFromResponse, useErrorReporter } from '@mdlinx/frontend-shared';
import { failed, Result, succeeded } from '@mdlinx/shared';
import {
  ChangeEmailError,
  useChangeEmailMutation,
  ChangeEmailMutationVariables,
  ChangeEmailMutation,
} from 'generated/client';
import { messages } from 'lib/messages';
import React, { FormEvent, useState } from 'react';

type Message = Result<string, string>;

export function translateChangeEmailError(error: ChangeEmailError): string {
  switch (error) {
    case ChangeEmailError.EmailExists:
      return messages.error.changeEmail.emailAlreadyExists;
    case ChangeEmailError.EmailInvalid:
      return messages.error.changeEmail.emailInvalid;
    case ChangeEmailError.Unknown:
      return messages.error.changeEmail.unknown;
  }
}

interface FetchResult<TData = Record<string, any>, TContext = Record<string, any>, TExtensions = Record<string, any>>
  extends ExecutionResult<TData> {
  data?: TData | null | undefined;
  extensions?: TExtensions;
  context?: TContext;
}

export const ChangeEmail: React.FC = () => {
  const [changeEmail] = useChangeEmailMutation();
  const errorReporter = useErrorReporter();
  const [newEmail, setNewEmail] = useState('');
  const [message, setMessage] = useState<Message | undefined>(undefined);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setMessage(undefined);
    changeEmail({ variables: { input: { newEmail } } })
      .then(r => extractDataFromResponse<ChangeEmailMutation>(r))
      .then(({ changeEmail: result }) => {
        switch (result.__typename) {
          case 'ChangeEmailSuccess':
            return setMessage(succeeded('Your email is successfully changed.'));
          case 'ChangeEmailFailure':
            return setMessage(failed(translateChangeEmailError(result.error)));
        }
      }, errorReporter.report);
  };

  return (
    <form onSubmit={onSubmit}>
      <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
      <button type="submit">Change</button>
      {message && (
        <div>
          {message.fold(
            msg => (
              <span>{msg}</span>
            ),
            err => (
              <span style={{ color: 'red' }}>{err}</span>
            ),
          )}
        </div>
      )}
    </form>
  );
};
